/**
 * ★ "ARCHIVE THIS ARTICLE", AT THE VERY FOOT OF A PUBLISHED ARTICLE'S OWN PAGE (design 24, T2; the
 * owner, 2026-10-11: "hide the archive button on the article list and leave it somewhere at the
 * bottom of the article details"). Archiving is a "just in case" action MoonX does not want used,
 * so it left the list card (../ArticleRowActions.tsx) for the end of the read view's right column
 * (./ArticleReadView.tsx), below "Version history" - on a phone, the last thing on the page.
 *
 * A quiet text control, not a pill. It opens the confirmation the card used to open, unchanged: its
 * words are the `archive` entry's of ../article-actions.ts, so they stay in one place - named here
 * by their keys, which carry a `_video` variant for a video's page. Nothing is
 * optimistic, as on the card: the call is `archiveOwnArticle`; a refusal (somebody moved the
 * article meanwhile) is said in words under the control and the page stays; once it is done the
 * author goes back to the list, where the card says "Archived" and offers "Unarchive".
 */
import { Alert, Stack, Text, UnstyledButton } from '@mantine/core';
import { describeProblem, useCabinetApi } from '../../../features/cabinet/api';
import { ConfirmAction } from '../../../features/cabinet/components';
import { useAction } from '../../../features/cabinet/state';
import { useNavigateToHref } from '../../../features/shell/in-app-navigation';
import { useTranslation } from '../../../i18n';
import { VIDEOS_HREF } from '../../videos/paths';
import { ARTICLE_ACTIONS } from '../article-actions';
import { usePieceContext } from './piece-context';
import classes from './editor.module.css';

/** The list the author goes back to once the article is archived. */
const ARTICLES_HREF = '/articles';

/** The `archive` row of the actions table: the confirmation's words and its button's label. */
const ARCHIVE = ARTICLE_ACTIONS.find((definition) => definition.kind === 'archive');

/**
 * A published video's read view draws this too (../../videos/VideoReadView.tsx), under the
 * video's piece context: the words say "video", and the author goes back to the Videos list.
 */
export function ArchiveThisArticle({ articleId }: { readonly articleId: string }) {
  const { t } = useTranslation('articles');
  const context = usePieceContext();
  const api = useCabinetApi();
  const navigate = useNavigateToHref();
  const archive = useAction(async () => await api.archiveOwnArticle(articleId));

  if (ARCHIVE?.confirm === undefined) {
    return null;
  }
  return (
    <Stack gap={8}>
      <ConfirmAction
        title={t('list.actions.archive.confirmTitle', { context })}
        body={t('list.actions.archive.confirmBody', { context })}
        confirmLabel={t(ARCHIVE.label)}
        destructive={ARCHIVE.destructive}
        busy={archive.isRunning}
        onConfirm={async () => {
          const moved = await archive.run();
          if (moved !== undefined) {
            navigate(context === 'video' ? VIDEOS_HREF : ARTICLES_HREF);
          }
        }}
        trigger={(open) => (
          <UnstyledButton className={classes.quietAction} onClick={open}>
            {t('editor.archive.button', { context })}
          </UnstyledButton>
        )}
      />
      {archive.error === undefined ? null : (
        <Alert
          variant="light"
          role="alert"
          title={t('common.didNotGoThrough')}
          withCloseButton
          closeButtonLabel={t('common.dismiss')}
          onClose={archive.clearError}
          styles={{
            root: {
              backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
              border: '1px solid oklch(var(--mx-danger) / 0.45)',
            },
            title: { color: 'oklch(var(--mx-danger))' },
          }}
        >
          <Text size="sm">{describeProblem(archive.error, context)}</Text>
        </Alert>
      )}
    </Stack>
  );
}
